/**
 * Stats Command
 * Show LLM usage statistics and limits
 */
import { Command } from 'commander';
import Table from 'cli-table3';
import chalk from 'chalk';
import { ConfigService } from '../services/config.service.js';
import { LlmProxyService } from '../services/llm-proxy.service.js';
import { ValidationError } from '../errors/index.js';
import type { UsageRecord } from '../types/llm.types.js';

/**
 * Handle stats command
 */
async function handleStatsCommand(): Promise<void> {
  const configService = new ConfigService();
  const config = await configService.load();

  if (!config) {
    throw new ValidationError(
      'Chưa xác thực. Vui lòng chạy "jai1 auth" trước.'
    );
  }

  const service = new LlmProxyService(config);

  console.log(chalk.bold('\n📊 Thống Kê Sử Dụng LLM'));
  console.log(chalk.dim('─'.repeat(45)));

  try {
    // Fetch data in parallel
    const [limits, usage7Days, usageToday] = await Promise.all([
      service.getLimits(),
      service.getUsage(7),
      service.getUsage(1),
    ]);

    // Get today's date in GMT+7 timezone
    const today = new Date().toLocaleDateString('en-CA', {
      timeZone: 'Asia/Ho_Chi_Minh',
    });

    console.log(chalk.cyan('\n📅 Khoảng thời gian: 7 ngày qua\n'));

    // Process usage data
    const usageByModel = new Map<string, { today: number; total7Days: number }>();

    // Calculate 7-day totals
    let total7DaysRequests = 0;
    usage7Days.data?.forEach((record: UsageRecord) => {
      if (!usageByModel.has(record.model)) {
        usageByModel.set(record.model, { today: 0, total7Days: 0 });
      }
      const modelData = usageByModel.get(record.model)!;
      modelData.total7Days += record.count;
      total7DaysRequests += record.count;
    });

    // Add today's usage
    usageToday.data?.forEach((record: UsageRecord) => {
      if (record.date === today) {
        if (!usageByModel.has(record.model)) {
          usageByModel.set(record.model, { today: 0, total7Days: 0 });
        }
        const modelData = usageByModel.get(record.model)!;
        modelData.today = record.count;
      }
    });

    // Display total summary
    console.log(chalk.bold('📈 Tổng quan sử dụng'));
    console.log(`   Tổng số yêu cầu (7 ngày): ${chalk.green(total7DaysRequests)}\n`);

    // Display per-model table
    console.log(chalk.bold('📦 Thống kê theo model\n'));

    // Create table with cli-table3
    const table = new Table({
      head: [
        chalk.bold('Model'),
        chalk.bold('Hôm nay'),
        chalk.bold('Giới hạn'),
        chalk.bold('Tổng 7 ngày'),
      ],
      style: {
        head: ['cyan'],
        border: ['gray'],
      },
      colWidths: [35, 14, 14, 14],
    });

    // Get all models from limits
    const allowedModels = limits.effectiveAllowedModels || [];
    const rateLimits = limits.effectiveRateLimits || {};

    if (allowedModels.length === 0) {
      table.push([
        { colSpan: 4, content: chalk.yellow('Không có model nào khả dụng') },
      ]);
    } else {
      allowedModels.forEach((modelId: string) => {
        const usage = usageByModel.get(modelId) || { today: 0, total7Days: 0 };
        const limit =
          rateLimits[modelId] || rateLimits[modelId.toLowerCase()] || 0;

        // Color code usage
        const usagePercent = limit > 0 ? usage.today / limit : 0;
        let todayDisplay = `${usage.today}/${limit}`;
        if (usagePercent >= 0.9) {
          todayDisplay = chalk.red(todayDisplay);
        } else if (usagePercent >= 0.7) {
          todayDisplay = chalk.yellow(todayDisplay);
        } else {
          todayDisplay = chalk.green(todayDisplay);
        }

        table.push([modelId, todayDisplay, `${limit}/ngày`, String(usage.total7Days)]);
      });
    }

    console.log(table.toString());
    console.log(
      chalk.dim('\n💡 Mẹo: Chạy "jai1 openai-keys" để xem danh sách model khả dụng')
    );
  } catch (error) {
    console.error(
      chalk.red('\n❌ Không thể lấy thống kê:'),
      error instanceof Error ? error.message : String(error)
    );
    console.log(chalk.dim('\n💡 Kiểm tra kết nối API với "jai1 status"'));
  }
}

/**
 * Create stats command
 */
export function createStatsCommand(): Command {
  const cmd = new Command('stats')
    .description('Hiển thị thống kê sử dụng LLM và giới hạn')
    .action(async () => {
      await handleStatsCommand();
    });

  return cmd;
}
